import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { Draft, enableMapSet } from 'immer';
import {
  UploadedFile,
  UploadedFileFromEntry,
} from '@ui/utils/files/uploaded-file';
import { UploadStrategy, UploadStrategyConfig } from './strategy/upload-strategy';
import { MessageDescriptor } from '@ui/i18n/message-descriptor';
import { FileEntry } from '../file-entry';
import { S3MultipartUpload } from './strategy/s3-multipart-upload';
import { Settings } from '@ui/settings/settings';
import { TusUpload } from './strategy/tus-upload';
import { ProgressTimeout } from './progress-timeout';
import { startUploading } from './start-uploading';
import { createUpload } from './create-file-upload';
import { nanoid } from 'nanoid';

enableMapSet();

export interface FileUpload {
  file: UploadedFile | UploadedFileFromEntry;
  percentage: number;
  bytesUploaded: number;
  status: 'pending' | 'inProgress' | 'aborted' | 'failed' | 'completed';
  errorMessage?: string | MessageDescriptor | null;
  entry?: FileEntry;
  request?: UploadStrategy;
  timer?: ProgressTimeout;
  options: UploadStrategyConfig;
  meta?: unknown;
  startedAt?: number; // ✅ add this line
  autoRetries?: number;
}

interface State {
  concurrency: number;
  fileUploads: Map<string, FileUpload>;
  // uploads with pending and inProgress status
  activeUploadsCount: number;
  completedUploadsCount: number;
  uploadSessionId: string | null;
}

const initialState: State = {
  concurrency: 3,
  fileUploads: new Map(),
  activeUploadsCount: 0,
  completedUploadsCount: 0,
  uploadSessionId: null,
};

interface Actions {
  uploadMultiple: (
    files: (File | UploadedFile)[] | FileList | AsyncIterable<UploadedFile>,
    options?: Omit<
      UploadStrategyConfig,
      // progress would be called for each upload simultaneously
      'onProgress' | 'showToastOnRestrictionFail'
    >,
  ) => string[];
  uploadSingle: (
    file: File | UploadedFile,
    options?: UploadStrategyConfig,
  ) => string;
  removeUpload: (id: string) => void;
  clearInactive: () => void;
  abortUpload: (id: string) => void;
  retryUpload: (id: string) => void;
  retryAllFailed: () => void;
  updateFileUpload: (id: string, state: Partial<FileUpload>) => void;
  addCompletedFileUpload: (entry: FileEntry) => void;
  getUpload: (id: string) => FileUpload | undefined;
  getCompletedFileEntries: () => FileEntry[];
  runQueue: () => Promise<void>;
  reset: () => void;
  getUploadSessionId: () => string | null;
  startUploadSession: () => string;
  cancelUploads: () => void;
  enqueueUpload: (id: string, state?: Partial<FileUpload>) => void;
}

export type FileUploadState = State & Actions;

export interface FileUploadStoreOptions {
  modifyUploadedFile?: (file: UploadedFile) => UploadedFile;
}

interface StoreProps {
  settings: Settings;
  options?: FileUploadStoreOptions;
}
export const createFileUploadStore = ({ settings, options }: StoreProps) => {
  const pendingQueue: FileUpload[] = [];
  const activeIds = new Set<string>();

  async function* normalizeFiles(
    files: any
  ): AsyncGenerator<any> {
    if (files && typeof files === 'object' && Symbol.asyncIterator in files) {
      yield* files;
    } else if (files && typeof files.length === 'number') {
      for (let i = 0; i < files.length; i++) {
        yield files[i];
      }
    }
  }

  return create<FileUploadState>()(
    immer((set, get) => {
      return {
        ...initialState,
        reset: () => {
          pendingQueue.length = 0;
          activeIds.clear();
          set(state => {
            state.uploadSessionId = null;
            state.fileUploads.clear();
            state.activeUploadsCount = 0;
            state.completedUploadsCount = 0;
          });
        },

        getUploadSessionId: () => {
          return get().uploadSessionId;
        },

        startUploadSession: () => {
          let sessionId = get().uploadSessionId;
          if (!sessionId) {
            sessionId = nanoid();
            set(state => {
              state.uploadSessionId = sessionId;
            });
          }
          return sessionId;
        },

        getUpload: uploadId => {
          return get().fileUploads.get(uploadId);
        },

        getCompletedFileEntries: () => {
          return [...get().fileUploads.values()]
            .filter(u => !!u.entry)
            .map(u => u.entry) as FileEntry[];
        },

        clearInactive: () => {
          pendingQueue.length = 0;
          activeIds.clear();
          set(state => {
            state.uploadSessionId = null;
            state.fileUploads.forEach((upload, key) => {
              if (upload.status !== 'inProgress') {
                state.fileUploads.delete(key);
              }
            });
            state.completedUploadsCount = 0;
            state.activeUploadsCount = [...state.fileUploads.values()].filter(
              u => u.status === 'inProgress' || u.status === 'pending'
            ).length;
          });
          get().runQueue();
        },

        abortUpload: id => {
          const upload = get().fileUploads.get(id);
          if (upload) {
            upload.request?.abort();
            get().updateFileUpload(id, { status: 'aborted', percentage: 0 });
            get().runQueue();
          }
        },
        retryUpload: (id: string) => {
          let pendingUpload: FileUpload | undefined;
          set((state) => {
            const upload = state.fileUploads.get(id);
            if (upload && (upload.status === 'failed' || upload.status === 'aborted')) {
              const updated = {
                ...upload,
                status: 'pending' as const,
                percentage: 0,
                bytesUploaded: 0,
                errorMessage: null,
                autoRetries: 0,
              };
              state.fileUploads.set(id, updated);
              state.activeUploadsCount += 1;
              pendingUpload = updated as any;
            }
          });
          if (pendingUpload) {
            pendingQueue.unshift(pendingUpload);
          }
          get().runQueue();
        },
        retryAllFailed: () => {
          const toRetry: FileUpload[] = [];
          set((state) => {
            state.fileUploads.forEach((upload, key) => {
              if (upload.status === 'failed' || upload.status === 'aborted') {
                const updated = {
                  ...upload,
                  status: 'pending' as const,
                  percentage: 0,
                  bytesUploaded: 0,
                  errorMessage: null,
                  autoRetries: 0,
                };
                state.fileUploads.set(key, updated);
                state.activeUploadsCount += 1;
                toRetry.push(updated as any);
              }
            });
          });
          pendingQueue.unshift(...toRetry);
          get().runQueue();
        },
        updateFileUpload: (id, newUploadState) => {
          set(state => {
            const fileUpload = state.fileUploads.get(id);
            if (fileUpload) {
              const oldStatus = fileUpload.status;
              const newStatus = newUploadState.status;

              state.fileUploads.set(id, {
                ...fileUpload,
                ...newUploadState,
              });

              // If status changed, update counters
              if (newStatus && newStatus !== oldStatus) {
                const wasActive = oldStatus === 'pending' || oldStatus === 'inProgress';
                const isActive = newStatus === 'pending' || newStatus === 'inProgress';

                if (wasActive && !isActive) {
                  state.activeUploadsCount = Math.max(0, state.activeUploadsCount - 1);
                } else if (!wasActive && isActive) {
                  state.activeUploadsCount += 1;
                }

                if (newStatus === 'completed') {
                  state.completedUploadsCount += 1;

                  // Prune old completed uploads to keep the store tiny!
                  const completed = [...state.fileUploads.values()]
                    .filter(u => u.status === 'completed')
                    .sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0));

                  if (completed.length > 100) {
                    const toRemove = completed.slice(0, completed.length - 100);
                    toRemove.forEach(u => {
                      state.fileUploads.delete(u.file.id);
                    });
                  }
                }
              }
            }
          });
        },

        addCompletedFileUpload: entry => {
          set(state => {
            state.fileUploads.set(`${entry.id}`, {
              file: new UploadedFileFromEntry(entry),
              status: 'completed',
              entry,
              percentage: 100,
              bytesUploaded: entry.file_size || 0,
              options: {},
              startedAt: Date.now(),
            });
            state.completedUploadsCount += 1;

            const completed = [...state.fileUploads.values()]
              .filter(u => u.status === 'completed')
              .sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0));

            if (completed.length > 100) {
              const toRemove = completed.slice(0, completed.length - 100);
              toRemove.forEach(u => {
                state.fileUploads.delete(u.file.id);
              });
            }
          });
        },

        uploadSingle: (file, userOptions) => {
          let sessionId = get().uploadSessionId;
          if (!sessionId) {
            sessionId = nanoid();
            set(state => {
              state.uploadSessionId = sessionId;
            });
          }

          const upload = createUpload(file, userOptions, options);
          pendingQueue.push(upload);

          set(state => {
            state.activeUploadsCount += 1;
          });

          get().runQueue();

          return upload.file.id;
        },

        removeUpload: (id: string) => {
          set(state => {
            const upload = state.fileUploads.get(id);
            if (upload) {
              if (upload.status === 'pending' || upload.status === 'inProgress') {
                state.activeUploadsCount = Math.max(0, state.activeUploadsCount - 1);
              } else if (upload.status === 'completed') {
                state.completedUploadsCount = Math.max(0, state.completedUploadsCount - 1);
              }
              state.fileUploads.delete(id);
            }
          });
        },

        uploadMultiple: (files, strategyConfig) => {
          let sessionId = get().uploadSessionId;
          if (!sessionId) {
            sessionId = nanoid();
            set(state => {
              state.uploadSessionId = sessionId;
            });
          }

          const knownLength = files && ('length' in files) ? files.length : null;

          if (knownLength !== null) {
            set(state => {
              state.activeUploadsCount += knownLength;
            });
          }

          const iterable = normalizeFiles(files);

          (async () => {
            let count = 0;
            let batchCount = 0;
            for await (const file of iterable) {
              if (get().uploadSessionId !== sessionId) {
                break;
              }

              const upload = createUpload(file, strategyConfig, options);
              pendingQueue.push(upload);
              batchCount++;

              if (count === 0) {
                if (knownLength === null) {
                  set(state => {
                    state.activeUploadsCount += batchCount;
                  });
                  batchCount = 0;
                }
                get().runQueue();
              } else if (count % 100 === 0) {
                if (knownLength === null) {
                  set(state => {
                    state.activeUploadsCount += batchCount;
                  });
                  batchCount = 0;
                }
                get().runQueue();
              }

              count++;
              if (count % 200 === 0) {
                await new Promise(resolve => setTimeout(resolve, 0));
              }
            }

            if (batchCount > 0 && get().uploadSessionId === sessionId) {
              if (knownLength === null) {
                set(state => {
                  state.activeUploadsCount += batchCount;
                });
              }
              get().runQueue();
            }
          })();

          return [];
        },

        runQueue: async () => {
          const state = get();
          const uploads = [...state.fileUploads.values()];
          const activeUploads = uploads.filter(u => u.status === 'inProgress');
          const startingCount = activeIds.size;

          // Force concurrency of 1 for the first file when uploading folders
          let concurrency = state.concurrency;
          const hasFolderUpload = pendingQueue.some(u => !!u.file.relativePath) ||
                                  uploads.some(u => !!u.file.relativePath);
          if (hasFolderUpload && state.completedUploadsCount === 0) {
            concurrency = 1;
          }

          if (activeUploads.length + startingCount < concurrency && startingCount < 1) {
            const next = pendingQueue.shift();
            if (next) {
              activeIds.add(next.file.id);
              if (!state.fileUploads.has(next.file.id)) {
                set(state => {
                  state.fileUploads.set(next!.file.id, next!);
                });
              }
              try {
                await startUploading(next, get());
              } finally {
                activeIds.delete(next.file.id);
              }
              get().runQueue();
            }
          }
        },

        cancelUploads: () => {
          pendingQueue.length = 0;
          activeIds.clear();
          set(state => {
            state.uploadSessionId = null;
          });
          get().fileUploads.forEach((upload) => {
            if (upload.status === 'inProgress' || upload.status === 'pending') {
              upload.request?.abort();
              get().updateFileUpload(upload.file.id, { status: 'aborted', percentage: 0 });
            }
          });
        },

        enqueueUpload: (id, newUploadState) => {
          let pendingUpload: FileUpload | undefined;
          set((state) => {
            const upload = state.fileUploads.get(id);
            if (upload) {
              const updated = {
                ...upload,
                status: 'pending' as const,
                percentage: 0,
                bytesUploaded: 0,
                errorMessage: null,
                ...newUploadState,
              };
              state.fileUploads.set(id, updated);
              pendingUpload = updated as any;
            }
          });
          if (pendingUpload) {
            if (!pendingQueue.some(u => u.file.id === id)) {
              pendingQueue.unshift(pendingUpload);
            }
          }
          get().runQueue();
        },
      };
    }),
  );
};